import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { images } from '../../utils/api';
import useUploadStore from '../../stores/uploadStore';
import DeleteConfirmationModal from '../DeleteConfirmationModal';

const ImageManager = ({ 
  coinId, 
  coinImages, 
  onImagesChange, 
  onImageClick,
  fileInputRef,
  setIsUploading
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  // Use individual selectors to prevent unnecessary re-renders
  const uploadStatus = useUploadStore(state => state.uploadStatus);
  const updateUpload = useUploadStore(state => state.updateUpload);
  const resetUploads = useUploadStore(state => state.resetUploads);

  // Reset upload store when component unmounts
  useEffect(() => {
    return () => {
      // Check for active uploads right before cleanup
      const hasActiveUploads = Object.values(uploadStatus).some(
        status => status === 'uploading'
      );
      if (hasActiveUploads) {
        resetUploads();
      }
    };
  }, [resetUploads]); // Only depend on resetUploads

  // Load images with better state management and race condition handling
  useEffect(() => {
    const loadImages = async () => {
      const loadingPromises = coinImages.map(async (image) => {
        const imageKey = `${image.id}`;
        
        // Skip if already loaded successfully
        if (imageUrls[imageKey] && imageUrls[imageKey] !== '/images/placeholder.svg') {
          return { imageKey, url: imageUrls[imageKey] };
        }
        
        setLoadingImages(prev => ({...prev, [imageKey]: true}));
        
        try {
          if (!image.file_path) {
            throw new Error('No file path provided');
          }
          
          const url = await images.getImage(image.file_path, image.source);
          if (!url) {
            throw new Error('No URL returned');
          }
          return { imageKey, url };
        } catch (err) {
          console.error(`Failed to load image ${image.id}:`, err);
          return { imageKey, url: '/images/placeholder.svg' };
        } finally {
          setLoadingImages(prev => ({...prev, [imageKey]: false}));
        }
      });
      
      // Wait for all images to load or fail
      const results = await Promise.all(loadingPromises);
      
      // Update URLs all at once to prevent partial states
      const newImageUrls = results.reduce((acc, { imageKey, url }) => {
        acc[imageKey] = url;
        return acc;
      }, {});
      
      setImageUrls(prev => ({...prev, ...newImageUrls}));
    };
    
    loadImages();
  }, [coinImages, images]);

  const handleFileSelect = async (event) => {
    const files = event.target.files;
    if (!files.length) return;

    // Check if adding these files would exceed the 40 image limit
    if (coinImages.length + files.length > 40) {
      alert('Cannot upload more images. Maximum of 40 images allowed per coin.');
      fileInputRef.current.value = '';
      return;
    }

    setIsUploading?.(true);
    const fileArray = Array.from(files);
    const csrfToken = localStorage.getItem('csrfToken');
    const uploadedImages = [];

    try {
      // Upload files one by one
      for (const file of fileArray) {
        try {
          updateUpload(file.name, { status: 'uploading', progress: 0 });
          
          // Use the images API utility which handles CSRF properly
          const result = await images.upload(coinId, [file]);
          uploadedImages.push(...result);
          
          updateUpload(file.name, { status: 'success', progress: 100 });
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
          updateUpload(file.name, { status: 'error', progress: 0 });
          // Continue with next file even if this one failed
        }
      }

      if (uploadedImages.length > 0) {
        onImagesChange([...coinImages, ...uploadedImages]);
      }
    } catch (err) {
      console.error('Failed to upload images:', err);
      alert('Failed to upload images. Please try again.');
    } finally {
      // Reset file input
      fileInputRef.current.value = '';
      
      // Clear upload states after a delay
      setTimeout(() => {
        resetUploads();
        setIsUploading?.(false);
      }, 2000);
    }
  };

  const handleDragEnd = async (result) => {
    setIsDragging(false);
    
    if (!result.destination) return;
    
    const newImages = Array.from(coinImages);
    const [reorderedImage] = newImages.splice(result.source.index, 1);
    newImages.splice(result.destination.index, 0, reorderedImage);
    
    // Keep original order in case of failure
    const originalImages = [...coinImages];
    
    // Optimistically update UI
    onImagesChange(newImages);
    
    // Update display order in backend
    try {
      await images.reorder(coinId, newImages.map(img => img.id));
    } catch (err) {
      console.error('Failed to reorder images:', err);
      // Revert to original order if backend update fails
      onImagesChange(originalImages);
      alert('Failed to reorder images. Please try again.');
    }
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);

  const [deleteErrorMessage, setDeleteErrorMessage] = useState('');

  const handleDeleteImage = useCallback(async () => {
    if (!imageToDelete) return;
    
    try {
      await images.delete(imageToDelete);
      // Remove the deleted image from the list
      const updatedImages = coinImages.filter(img => img.id !== imageToDelete);
      onImagesChange(updatedImages);
    } catch (err) {
      console.error('Failed to delete image:', err);
      alert('Failed to delete image. Please try again.');
    } finally {
      setIsDeleteModalOpen(false);
      setImageToDelete(null);
      setDeleteErrorMessage('');
    }
  }, [imageToDelete, coinImages, onImagesChange]);

  return (
    <div className="space-y-4">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        multiple
        accept="image/*"
      />
      <DragDropContext onDragStart={() => setIsDragging(true)} onDragEnd={handleDragEnd}>
        <Droppable droppableId="coin-images" direction="horizontal">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {coinImages.map((image, index) => (
                <Draggable key={image.id} draggableId={image.id.toString()} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={`relative group ${snapshot.isDragging ? 'z-50' : ''}`}
                    >
                      <div 
                        className={`aspect-w-1 aspect-h-1 w-full h-48 overflow-hidden rounded-lg bg-white shadow-lg transition-transform duration-200 ease-in-out ${
                          isDragging ? '' : 'hover:scale-105'
                        } cursor-pointer`}
                        onClick={() => onImageClick && onImageClick(index)}
                      >
                        <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg">
                          {loadingImages[`${image.id}`] ? (
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                          ) : (
                          <img
                            src={imageUrls[`${image.id}`] || '/images/placeholder.svg'}
                            alt={`Coin image`}
                            title={image.image_type || `Image ${index + 1}`}
                            className="w-full h-full object-contain p-2 group-hover:cursor-pointer"
                            onError={(e) => {
                              console.error('Image load error for:', {
                                id: image.id,
                                path: image.file_path,
                                url: imageUrls[`${image.id}`]
                              });
                              e.target.onerror = null;
                              e.target.src = '/images/placeholder.svg';
                            }}
                          />
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setImageToDelete(image.id);
                            setIsDeleteModalOpen(true);
                          }}
                          className="absolute top-2 right-2 text-white hover:text-red-500 transition-colors duration-200 bg-black/50 rounded-full p-1"
                          title="Delete image"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        title="Delete Image"
        message={deleteErrorMessage || "Are you sure you want to delete this image? This action cannot be undone."}
        onConfirm={deleteErrorMessage ? undefined : handleDeleteImage}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setImageToDelete(null);
          setDeleteErrorMessage('');
        }}
        confirmText={deleteErrorMessage ? undefined : "Delete"}
        cancelText={deleteErrorMessage ? "Close" : "Cancel"}
      />
    </div>
  );
};

export default ImageManager;
